import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7180244c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "header flex w-full items-center" }
const _hoisted_2 = { class: "flex-auto" }
const _hoisted_3 = { class: "organizations flex w-full" }
const _hoisted_4 = {
  key: 0,
  class: "organization-details flex-1"
}
const _hoisted_5 = { class: "flex rounded-t-lg border bg-gray-100 text-gray-500 align-middle justify-between px-3 py-3" }
const _hoisted_6 = { class: "flex-1" }

import { onBeforeMount, Ref, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { XIcon } from "@heroicons/vue/outline";
import router from "@/router";
import OrganizationsList from "@/components/administration/organizations/list/list.vue";
import CreateOrganizationDialog from "@/components/administration/organizations/create-organization-dialog.vue";
import IOrganization, {
	ICreateOrganizationData,
	IUpdateOrganizationData,
} from "@/interfaces/organizations/iOrganization";
import { createOrganization, getOrganizations, updateOrganization } from "@/services/organizationsApi";
import { useExecuteAsync } from "@/composables/useExecuteAsync";

type LoadingFields = "list" | "save";


export default /*#__PURE__*/_defineComponent({
  __name: 'Organizations',
  setup(__props) {

const route = useRoute();
const localization = useI18n();
const { loading, executeAsync } = useExecuteAsync();

const organizations: Ref<IOrganization[]> = ref([]);
const selectedOrganizationId: Ref<string | undefined> = ref((route.params.id as string) || undefined);
const dialogOpen = ref(false);
const editOrganization: Ref<IOrganization | undefined> = ref(undefined);
const detailsReloadKey = ref(0);

const fetchOrganizations = async () => {
	try {
		organizations.value = (await executeAsync<IOrganization[], LoadingFields>("list", getOrganizations)) ?? [];
	} catch {
		// executeAsync already showed the error toast; keep the current list
	}
};

onBeforeMount(fetchOrganizations);

// the route is the single source of the selection: covers X, the Organizations tab and browser Back
watch(
	() => route.params.id,
	(id) => {
		if (route.name !== "Organizations" && route.name !== "Organization") {
			return;
		}

		selectedOrganizationId.value = (id as string) || undefined;
	}
);

const handleDetailsOpen = (id: string) => router.push({ name: "Organization", params: { id } });

const handleDetailsClose = () => router.push({ name: "Organizations" });

const handleOpenCreate = () => {
	editOrganization.value = undefined;
	dialogOpen.value = true;
};

const handleOpenEdit = (id: string) => {
	editOrganization.value = organizations.value.find((o) => o.id === id);
	dialogOpen.value = true;
};

const handleCloseDialog = () => {
	dialogOpen.value = false;
	editOrganization.value = undefined;
};

const handleSubmit = async (data: ICreateOrganizationData | IUpdateOrganizationData) => {
	const isEdit = !!editOrganization.value;

	try {
		await executeAsync<void, LoadingFields>("save", () =>
			isEdit ? updateOrganization(data as IUpdateOrganizationData) : createOrganization(data)
		);
	} catch {
		// executeAsync already showed the error toast; keep the dialog open
		return;
	}

	handleCloseDialog();
	await fetchOrganizations();

	if (isEdit) {
		detailsReloadKey.value++;
	}
};

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h3", _hoisted_2, _toDisplayString(_unref(localization).t("administration.organizations.title")), 1),
      _createElementVNode("button", {
        class: "btn flex-none",
        onClick: handleOpenCreate
      }, _toDisplayString(_unref(localization).t("administration.organizations.create")), 1)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", {
        class: _normalizeClass(["organization-list flex-1", selectedOrganizationId.value && 'condensed'])
      }, [
        _createVNode(OrganizationsList, {
          items: organizations.value,
          "selected-organization-id": selectedOrganizationId.value,
          onOnDetailsClick: handleDetailsOpen,
          onOnEditClick: handleOpenEdit
        }, null, 8, ["items", "selected-organization-id"])
      ], 2),
      (selectedOrganizationId.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("label", _hoisted_6, _toDisplayString(_unref(localization).t("administration.organizations.details-title")), 1),
              _createVNode(_unref(XIcon), {
                class: "h-8 w-8 cursor-pointer",
                onClick: handleDetailsClose
              })
            ]),
            _createVNode(_component_router_view, { "reload-key": detailsReloadKey.value }, null, 8, ["reload-key"])
          ]))
        : _createCommentVNode("", true)
    ]),
    (dialogOpen.value)
      ? (_openBlock(), _createBlock(CreateOrganizationDialog, {
          key: 0,
          loading: _unref(loading).save,
          "edit-mode": !!editOrganization.value,
          "organization-data": editOrganization.value,
          onOnSubmit: handleSubmit,
          onOnCancel: handleCloseDialog
        }, null, 8, ["loading", "edit-mode", "organization-data"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})